import React from 'react';
import Image from 'components/NewsImage';
import Link from 'components/Link';
import { getNewsPage } from 'constants/pages';
import { renderEmailAddresses } from 'components/EmailLink/richText';
import type { PortableTextComponents } from '@portabletext/react';

function NewsBodyImage({ value }: any) {
  const { src, width, height } = value.localizedAsset;
  const crop = value.crop;
  const cropped =
    crop && width && height && [crop.left, crop.right, crop.top, crop.bottom].some(Boolean);
  const left = Math.round((crop?.left || 0) * width);
  const top = Math.round((crop?.top || 0) * height);
  const retainedWidth = Math.round(width - (crop?.right || 0) * width - left);
  const retainedHeight = Math.round(height - (crop?.bottom || 0) * height - top);
  return (
    <div className="relative mx-auto my-6 h-[300px] w-full overflow-hidden rounded-lg sm:h-[400px] md:h-[500px]">
      {cropped ? (
        // Sanity applied the authored crop before the fixed-height center-cover box.
        // Localized assets retain the full image, so keep that same visible rectangle.
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox={`${left} ${top} ${retainedWidth} ${retainedHeight}`}
          preserveAspectRatio="xMidYMid slice"
          role="img"
          aria-label={value.alt || 'Image'}
        >
          <title>{value.alt || 'Image'}</title>
          <image href={src} width={width} height={height} />
        </svg>
      ) : (
        <Image
          src={src}
          alt={value.alt || 'Image'}
          title={value.alt || 'Image'}
          fill
          className="object-cover"
          sizes="(max-width: 800px) 100vw, 800px"
        />
      )}
    </div>
  );
}

// The news Portable Text presentation before the Firestore cutover (b37324466^).
// Keep its layout here; changing the content source must not change the article UI.
export const newsComponents: PortableTextComponents = {
  types: {
    image: NewsBodyImage,
    table: ({ value }: any) => {
      const [heading, ...body] = value.rows || [];
      if (!heading || !body.length) return null;
      return (
        <table className="my-4 w-full border border-palette-border">
          {value.caption && (
            <caption className="font-incognito my-1 text-lg font-medium">
              {renderEmailAddresses(value.caption)}
            </caption>
          )}
          <thead className="border-b border-palette-border bg-palette-black-4 text-left">
            <tr className="divide-x divide-palette-border">
              {heading.cells.map((cell: string, index: number) => (
                <th
                  key={index}
                  scope="col"
                  className="font-incognito px-3 py-2 font-semibold text-palette-black-9"
                >
                  {renderEmailAddresses(cell)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row: any, index: number) => (
              <tr
                key={index}
                className="divide-x divide-palette-border border border-palette-border"
              >
                {row.cells.map((cell: string, column: number) => (
                  <td key={column} className="px-3 py-2">
                    {renderEmailAddresses(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    },
  },
  block: {
    h1: ({ children }) => (
      <h1 className="mb-6 mt-8 text-4xl font-bold text-white first:mt-0">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="mb-4 mt-8 text-2xl font-bold text-white first:mt-0 sm:text-3xl md:text-4xl">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mb-3 mt-6 text-xl font-bold text-white first:mt-0 sm:text-2xl md:text-3xl">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="mb-3 mt-4 text-lg font-bold text-white first:mt-0 sm:text-xl md:text-2xl">
        {children}
      </h4>
    ),
    normal: ({ children }) => <p className="mb-4 leading-relaxed text-white">{children}</p>,
    blockquote: ({ children }) => (
      <blockquote className="my-6 rounded-r-lg border-l-4 border-blue-500 bg-gray-800/20 py-2 pl-6 italic text-white/90">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="mb-4 list-inside list-disc space-y-2 pl-4 text-white">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="mb-4 list-inside list-decimal space-y-2 pl-4 text-white">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="leading-relaxed text-white">{children}</li>,
    number: ({ children }) => <li className="leading-relaxed text-white">{children}</li>,
  },
  marks: {
    link: ({ children, value }) => (
      <a
        href={value.href}
        rel={value.href?.startsWith('/') ? undefined : 'noopener noreferrer'}
        target={value.href?.startsWith('/') ? undefined : '_blank'}
        className="text-blue-400 underline transition-colors hover:text-blue-300"
      >
        {children}
      </a>
    ),
    internalLink: ({ children, value }) => (
      <Link
        href={getNewsPage(value.slug?.current)}
        className="text-blue-400 underline transition-colors hover:text-blue-300"
      >
        {children}
      </Link>
    ),
    strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
    em: ({ children }) => <em className="italic text-blue-300">{children}</em>,
    highlight: ({ children }) => <span className="font-bold text-blue-500">{children}</span>,
  },
};
